import { createSlice, PayloadAction } from '@reduxjs/toolkit';

type NotificationType = 'message' | 'chat_message' | 'order' | 'price_drop';

export interface Notification {
    id: string;
    created_at: string;
    updated_at?: string;
    request_id?: string;
    sender_id?: string;
    recipient_id?: string;
    type: NotificationType;
    status: 'read' | 'unread';
    message?: string;
    read_at?: string;
    responded_at?: string;
    data?: {
        chatId?: string;
        orderId?: string;
        productId?: string;
        request_id?: string;
        vendor_id?: string;
        vendor_name?: string;
        product_title?: string;
        buyer_id?: string;
        items?: Array<{
            product_id: string;
            name: string;
            quantity: number;
            [key: string]: any;
        }>;
        is_available?: boolean;
        notes?: string;
        [key: string]: any;
    };
    // Legacy fields for backward compatibility
    title?: string;
    description?: string;
    time?: string;
}

interface NotificationsState {
    list: Notification[];
}

const initialState: NotificationsState = {
    list: [
        {
            id: '1',
            created_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
            type: 'message',
            status: 'unread',
            message: 'You have a new message from a seller',
            title: 'New Message',
            description: 'You have a new message from a seller',
            time: '2 hours ago',
            data: { chatId: '123' },
        },
        {
            id: '2',
            created_at: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
            type: 'order',
            status: 'read',
            message: 'Your order has been shipped',
            title: 'Order Update',
            description: 'Your order has been shipped',
            time: '1 day ago',
            data: { orderId: '456' },
        },
        {
            id: '3',
            created_at: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
            type: 'price_drop',
            status: 'unread',
            message: 'A product in your wishlist is now on sale',
            title: 'Price Drop',
            description: 'A product in your wishlist is now on sale',
            time: '2 days ago',
            data: { productId: '789' },
        },
    ],
};

const notificationsSlice = createSlice({
    name: 'notifications',
    initialState,
    reducers: {
        addNotification: (state, action: PayloadAction<Notification>) => {
            state.list.unshift(action.payload);
        },
        removeNotification: (state, action: PayloadAction<string>) => {
            state.list = state.list.filter((n) => n.id !== action.payload);
        },
        clearNotifications: (state) => {
            state.list = [];
        },
        fetchNotifications: (state) => {
            // Implementation handled by useNotifications hook - state unused
            console.log('Fetching notifications via Redux');
        },
        markAsRead: (state, action: PayloadAction<string>) => {
            const notification = state.list.find(n => n.id === action.payload);
            if (notification) {
                notification.status = 'read';
            }
        },
        markAllAsRead: (state) => {
            state.list.forEach(notification => {
                notification.status = 'read';
            });
        },
        setNotifications: (state, action: PayloadAction<Notification[]>) => {
            state.list = action.payload;
        },
        updateNotificationStatus: (state, action: PayloadAction<{id: string, status: 'read' | 'unread'}>) => {
            const notification = state.list.find(n => n.id === action.payload.id);
            if (notification) {
                notification.status = action.payload.status;
            }
        },
    },
});

export const {
    addNotification,
    removeNotification,
    clearNotifications,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    setNotifications,
    updateNotificationStatus,
} = notificationsSlice.actions;

export const notificationsReducer = notificationsSlice.reducer;